import { useEffect, useRef } from 'react'
import { useFeatureTogglesStore } from 'stores/featureTogglesStore'
import { HANDTALK_TOKEN } from 'config/vars'

declare global {
  interface Window {
    HT?: new (config: HandTalkConfig) => HandTalkInstance
    htInstance?: HandTalkInstance
    _handTalkReady?: boolean
  }
}

interface HandTalkConfig {
  token: string
  avatar?: 'HUGO' | 'MAYA'
  side?: 'right' | 'left'
  align?: 'default' | 'top' | 'bottom'
  zIndex?: number
  mobileEnabled?: boolean
  addonsEnabled?: boolean
  highContrast?: boolean
  language?: 'ptBR-bzs' | 'en-ase'
  colorButton?: 'a11yColorMain' | 'neutralLowMain' | 'a11yColorLight'
}

interface HandTalkInstance {
  translate?: (text: string) => void
}

const HANDTALK_SDK_URL = 'https://plugin.handtalk.me/web/latest/handtalk.min.js'

const HANDTALK_HOST_ORIGIN = 'https://livros.arvore.com.br'

const HANDTALK_HOST_PATH = '/app-v2/vlibras-host.html'

const HANDTALK_FRAME_ID = 'arvore-handtalk-frame'

const PARENT_MESSAGE_SOURCE = 'arvore-handtalk'

const HOST_MESSAGE_SOURCE = 'arvore-handtalk-host'

const ARVORE_DOMAIN = 'arvore.com.br'

const HANDTALK_CONFIG: HandTalkConfig = {
  token: HANDTALK_TOKEN,
  avatar: 'HUGO',
  side: 'right',
  align: 'default',
  zIndex: 1000000,
  mobileEnabled: true,
  addonsEnabled: true
}

type HandTalkFrameBounds = {
  top: number
  left: number
  right: number
  bottom: number
} | null

const isHandTalkAuthorizedOrigin = () => {
  const { hostname } = window.location

  return hostname === ARVORE_DOMAIN || hostname.endsWith(`.${ARVORE_DOMAIN}`)
}

const applyFrameBounds = (
  frame: HTMLIFrameElement,
  bounds: HandTalkFrameBounds
) => {
  if (!bounds) {
    frame.style.pointerEvents = 'none'
    frame.style.clipPath = ''
    return
  }

  frame.style.pointerEvents = 'auto'
  frame.style.clipPath = `inset(${bounds.top}px ${bounds.right}px ${bounds.bottom}px ${bounds.left}px)`
}

const handleHostMessage = (event: MessageEvent) => {
  if (event.origin !== HANDTALK_HOST_ORIGIN) return
  if (!event.data || event.data.source !== HOST_MESSAGE_SOURCE) return

  const frame = document.getElementById(
    HANDTALK_FRAME_ID
  ) as HTMLIFrameElement | null

  if (!frame) return

  if (event.data.type === 'ready') {
    frame.contentWindow?.postMessage(
      {
        source: PARENT_MESSAGE_SOURCE,
        type: 'activate',
        config: HANDTALK_CONFIG
      },
      HANDTALK_HOST_ORIGIN
    )
    return
  }

  if (event.data.type === 'bounds') {
    applyFrameBounds(frame, event.data.bounds)
    return
  }

  if (event.data.type === 'error') {
    console.warn('HandTalk não autenticou:', event.data.message)
  }
}

const mountHandTalkFrame = () => {
  if (document.getElementById(HANDTALK_FRAME_ID)) return

  const frame = document.createElement('iframe')

  frame.id = HANDTALK_FRAME_ID
  frame.title = 'VLibras'
  frame.src = `${HANDTALK_HOST_ORIGIN}${HANDTALK_HOST_PATH}?parentOrigin=${encodeURIComponent(window.location.origin)}`
  frame.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;border:0;background:transparent;pointer-events:none;z-index:2147483000;'

  window.addEventListener('message', handleHostMessage)
  document.body.appendChild(frame)
}

const HandTalkWidget = () => {
  const { featureToggles } = useFeatureTogglesStore()
  const scriptLoaded = useRef(false)

  useEffect(() => {
    if (!featureToggles.vLibrasEnabled || scriptLoaded.current) return
    if (!HANDTALK_TOKEN || !isHandTalkAuthorizedOrigin()) return

    const script = document.createElement('script')
    script.src = HANDTALK_SDK_URL
    script.async = true

    script.onload = () => {
      window._handTalkReady = true
    }

    script.onerror = () => {
      scriptLoaded.current = false
    }

    document.body.appendChild(script)
    scriptLoaded.current = true

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script)
      }
    }
  }, [featureToggles.vLibrasEnabled])

  return null
}

export const activateHandTalk = () => {
  if (!HANDTALK_TOKEN) return

  if (!isHandTalkAuthorizedOrigin()) {
    mountHandTalkFrame()
    return
  }

  if (window.htInstance) return

  if (window._handTalkReady && window.HT) {
    window.htInstance = new window.HT(HANDTALK_CONFIG)
  }
}

export default HandTalkWidget
